import { PageMetaData, useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { SearchPageTrackingFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchPageTrackingFragmentFragmentDoc';

import type { FC } from 'react';
import { useEffect } from 'react';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';

/* GraphQL */ `
  fragment SearchPageTrackingFragment on EpsQueryResult {
    __typename
    ... on SearchResultBase {
      tracking {
        searchId
        landmarks
      }
    }

    ... on ProperCategoryQueryResult {
      breadcrumbs {
        id
        name
      }
    }
  }
`;

export type TrackingProps = {
  maskedResult: FragmentType<typeof SearchPageTrackingFragmentFragmentDoc>;
};

/**
 * Search Tracking component for pushing the following events to the GTM datalayer
 * - 'search-page-landmark'
 * - 'pageMetaData'
 * */
export const Tracking: FC<TrackingProps> = ({ maskedResult }) => {
  const result = getFragmentData(SearchPageTrackingFragmentFragmentDoc, maskedResult);

  const dispatchGTMEvent = useTracking();
  const query = useOriginalSearchQuery() || undefined;
  const { searchId, landmarks } = ('tracking' in result && result.tracking) || {
    searchId: undefined,
    landmarks: undefined,
  };

  useEffect(() => {
    if (!landmarks?.length) return;

    // NOTE: landmarks should be non-optional strings, but resolver schema is currently incorrect
    const validLandmarks = landmarks.filter((x): x is string => !!x);

    for (const url of validLandmarks) {
      dispatchGTMEvent({ event: 'search-page-landmark', eventValue: url });
    }
  }, [dispatchGTMEvent, landmarks]);

  // TODO i18n, currently only german
  const categoryPath =
    'breadcrumbs' in result
      ? result.breadcrumbs.map((item) => ({
          id: item.id,
          name: item.name === 'Startseite' ? 'storefront' : item.name,
        }))
      : undefined;

  const categoryPathHash = categoryPath
    ? categoryPath.map((item) => (item.name === 'Startseite' ? 'storefront' : item.name)).join('/')
    : query;

  let pageType: string;
  switch (result.__typename) {
    case 'BrandsResult':
    case 'CategoryResult': {
      pageType = 'productlist';
      break;
    }
    case 'LandingPageResult': {
      pageType = 'sis';
      break;
    }
    case 'SuccessfulSearchResult':
    case 'EmptySearchResult': {
      pageType = 'serp';
      break;
    }
    case 'SuggestionPreviewSearchResult': {
      pageType = 'searchbuddy';
      break;
    }
    default: {
      pageType = 'unknown';
      break;
    }
  }

  return (
    <PageMetaData
      page={{ type: pageType }}
      query={query}
      searchId={searchId}
      categoryPath={categoryPath}
      pathname={categoryPathHash}
    />
  );
};
